import React, { useState, useEffect } from 'react';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  CircularProgress,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import store from '../../redux/store'
import StreamCard from './StreamCard';
import { poll } from '@hotglue/common';
import swal from 'sweetalert';
import { connect } from 'react-redux';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { discoverLinkedSourceCatalog, pollDiscoverLinkedSource } from '../../api/client';

import { doUpdateLinkedSource, fetchLinkedSource as doFetchLinkedSource } from '../../redux/actions/network.actions';
import * as UIActions from '../../redux/actions/ui.actions';
import LoadingDial from '../loading/LoadingDial';
import { currentSelection, getPrimaryKeys, sortStreams, toFieldMap, toSourceMap } from './helper';
import {SearchRounded, RefreshRounded as RefreshIcon } from '@mui/icons-material';
import { Alert } from '../popupAlerts';
import { getListener } from '../../redux/selectors';

const FieldMapSelector = (props) => {
  const {
    fetchLinkedSource,
    updateLinkedSource,
    setFieldMapSelection,
    source,
    fieldMap: storedFieldMap,
    fieldMapSelection,
    flowId,
    history,
    filter,
    tenantId,
    localization,
  } = props;
  if (!source)
    return (
      <Box display="flex" flex={1} alignItems="center" justifyContent="center">
        <LoadingDial />
      </Box>
    );
  else {
    const [allStreams, setAllStreams] = useState();
    const [fieldMap, setFieldMap] = useState();
    const [visibleStreams, setVisibleStreams] = useState();
    const [isRefreshing, setRefreshing] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [hasFetched, setFetched] = useState(false);
    const [refresh, hardRefresh] = useState(false);
    const [primaryKeys, setPrimaryKeys] = useState([])
    const [firstLoadComplete, setFirstLoadComplete] = useState(false);
    const listener = getListener();
    const currentSelected = fieldMap ? currentSelection(fieldMap) : new Set();

    useEffect(() => {
      if (source && !source.catalog) {
        // Should at least have {} if it fetched
        fetchLinkedSource(source.tap, tenantId);
      }
    }, [source]);

    useEffect(() => {
      if (source.catalog) {
        const { catalog } = source;
        if (!hasFetched) {
          // If catalog is empty there should be an ongoing discover task
          onPoll();
          return;
        }

        let allStreams = (catalog && catalog.streams && catalog.streams.map((s) => s.stream)) || [];
        // Filter the streamas if filter is specified
        allStreams = allStreams.filter((value) => !filter?.fieldMapFilter || filter.fieldMapFilter(flowId, value));

        setAllStreams(allStreams);
        setVisibleStreams(allStreams);
        if(source.catalog){
          setPrimaryKeys(getPrimaryKeys(source.catalog));
        }
      }
    }, [source.catalog]);

    useEffect(() => {
      if (!allStreams) return;
      setVisibleStreams(allStreams.sort(sortStreams(currentSelected)));
      setFirstLoadComplete(true);
    }, [firstLoadComplete, allStreams]);

    useEffect(() => {
      if (!fieldMapSelection) {
        const fieldMap = toFieldMap(source.catalog, source.fieldMap, flowId, filter);

        if (Object.values(fieldMap).length > 0) {
          setFieldMap(fieldMap);
          setFieldMapSelection(fieldMap);
        }
      } else setFieldMap(fieldMapSelection);
    }, [source, source.fieldMap, source.catalog, flowId, filter]);
    //set the fieldmap..

    const filterStreamList = (e) => {
      const value = e.target.value;
      if (value == '') {
        setVisibleStreams(allStreams.sort(sortStreams));
        return;
      }
      const filterStreams =
        allStreams &&
        allStreams.filter((stream) => {
          if (!value || value.trim().length == 0) {
            return currentSelected.has(stream);
          }

          return stream.toLowerCase().search(value.toLowerCase()) !== -1;
        });

      setVisibleStreams(filterStreams && filterStreams.sort(sortStreams));
    };

    // Make sure that already selected fields are shown first.

    const handleSelect = (stream) => {
      const newFieldMap = Object.assign({}, fieldMap);
      // prevent race condition
      if (fieldMap && stream && fieldMap[stream]) {
        fieldMap[stream].isSelected = !fieldMap[stream]?.isSelected;
        fieldMap[stream].selected = ['*'];
        fieldMap[stream].notSelected = fieldMap[stream]?.all || [];
      } else {
        throw new Error(`Cannot find stream in fieldMap. Current stream: ${stream}. Fieldmap state: ${JSON.stringify(fieldMap)}. Current store: ${JSON.stringify(store.getState())}`);
      }

      setFieldMap(newFieldMap);
      setFieldMapSelection(newFieldMap);
    };

    const handleDone = async (e) => {
      setSaving(true);

      try {
        const res = await updateLinkedSource(source.tap, toSourceMap(fieldMap), tenantId);
        if (res.status === 'error') throw new Error(res.payload);
        Alert({
          text: 'Fields Mapped Successfully',
          type: 'success',
        });
        UIActions.clearFieldMapSelection();
        await fetchLinkedSource(source.tap);

        if (listener && listener.onFieldMapSave) {
          listener.onFieldMapSave(source.tap, flowId, tenantId);
        }
      } catch (e) {
        Alert({ type: 'error', text: `Something did not work right ${e.toString()}` });
      } finally {
        setSaving(false);
      }
    };

    const onPoll = async () => {
      setRefreshing(true);

      const { future, cancel } = poll({
        request: () => {
          return pollDiscoverLinkedSource(source.tap);
        },
        pollingPeriod: 1000,
        shouldStop: ({ status }) => {
          return status == null || status == 'STOPPING' || status == 'DEPROVISIONING' || status == 'STOPPED';
        },
      });

      future
        .then((response) => {
          fetchLinkedSource(source.tap, tenantId);
          setRefreshing(false);
          setFetched(true);
        })
        .catch((e) => {
          setRefreshing(false);
        });
    };

    const onRefreshCatalog = (e) => {
      e.stopPropagation();

      if (isRefreshing) {
        return;
      }

      swal({
        title: 'Are you sure?',
        text: 'This call will re-read any fields on the server and will take a few minutes.',
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          setRefreshing(true);
          discoverLinkedSourceCatalog(source.tap)
            .then(() => {
              onPoll();
            })
            .catch((err) => {
              console.error('Failed to refresh', err);
              Alert({ text: `Failed to refresh: ${err}`, type: 'error' });
              setRefreshing(false);
            });
        }
      });
    };

    if (!visibleStreams || !hasFetched || isRefreshing)
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            alignItems: 'center',
            width: '100%',
            height: '90%',
            flexDirection: 'column',
          }}
        >
          <CircularProgress />
          <Typography variant="h1">{localization?.fieldMap?.loadingTitle ?? 'Fetching available data...'}</Typography>
          <Typography variant="body1" color="text.light">
            {localization?.fieldMap?.loadingDescription ?? "Hang tight, it's going to be quick."}
          </Typography>
        </Box>
      );

    const Row = ({ index, style }) => {
      const stream = visibleStreams[index];
      return (
        <React.Fragment key={index}>
          <StreamCard
            {...props}
            style={style}
            stream={stream}
            fieldMap={fieldMap}
            isSelected={currentSelected.has(stream)}
            onSelect={handleSelect}
            primaryKeys={primaryKeys}
          />
        </React.Fragment>
      );
    };

    return (
      <Box display="flex" flexDirection="column" width="100%" style={{ flex: 1 }}>
        {isSaving && <LoadingDial />}
        <Box display={isSaving ? 'none' : 'flex'} flexDirection="column" width="100%">
          <Box display="flex" flexDirection="row" mt={2}>
            <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
              <Typography variant="body1" fontWeight={500}>Field Map</Typography>
              <Typography variant="body2" color="text.lightish">
                Select the fields you want to load
              </Typography>
            </Box>

            <Button
              variant="outlined"
              color="text"
              onClick={onRefreshCatalog}
              sx={{
                opacity: 0.75,
                border: ({ palette }) => `1px solid ${palette.common.outline}`,
                px: 3,
                fontWeight: 400,
                display: 'flex',
                fontSize: '0.875rem',
                maxHeight: '40px',
                gap: 1,
              }}
              disabled={isRefreshing}
            >
              Refresh
              {isRefreshing ? <CircularProgress color="text" size={16} /> : <RefreshIcon sx={{ fontSize: '16px' }} />}
            </Button>
          </Box>
          <TextField
            label="Search"
            id="search"
            type="search"
            variant="standard"
            color="text"
            placeholder="Data stream/table name"
            onChange={filterStreamList}
            InputProps={{
              endAdornment: <SearchRounded sx={{ fontSize: '16px' }} />,
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mt: 2, opacity: 0.75 }}
          />
        </Box>
        <Box style={{ paddingTop: '16px', flexGrow: 1, flex: 1 }}>
          <AutoSizer>
            {({ height, width }) => (
              <List height={height} width={width} itemCount={visibleStreams.length} itemSize={100}>
                {Row}
              </List>
            )}
          </AutoSizer>
        </Box>

        <Divider />

        <Box display="flex" justifyContent="space-between" py={2}>
          <Button
            variant="outlined"
            disabled={isSaving}
            color="text"
            onClick={() => UIActions.goBack()}
            sx={{ px: 2, opacity: 0.75 }}
          >
            {'Back'}
          </Button>
          <Button variant="contained" color="primary" onClick={handleDone} disabled={isSaving} sx={{ px: 4 }}>
            Save Changes
          </Button>
        </Box>
      </Box>
    );
  }
};

function mapStateToProps(state, props) {
  const tap = state.sources.selectedSource ? state.sources.selectedSource.tap : '';
  const entity = state.entities.selectedEntity;

  let source = state.sources.linked[tap];
  let fieldMap = state.sources.linked[tap]?.fieldMap;

  if (state.sources.linkedList.length > 0) {
    source = state.sources.linkedList.find((x) => x.tenant_id === entity.tenant_id && x.tap === tap);
    fieldMap = source?.fieldMap;
  }

  const result = {
    linkedSource: source,
    supportedSource: state.sources.supported,
    tenantId:
      state.entities.selectedEntity.tenant_id ||
      state.sources.selectedSource?.tenant_id ||
      state.configuration.tenant_id,
    envId: state.configuration.env_id,
    flowId: state.flows.selectedFlow,
    fieldMapSelection: state.sources.fieldMapSelection,
    history: state.navigation.history,
    filter: state.configuration.filter,
    localization: state.configuration.localization,
  };

  if (tap) {
    return {
      ...result,
      source,
      fieldMap,
    };
  }

  return result;
}

function mapDispatchToProps(dispatch) {
  return {
    updateLinkedSource: (tap, fieldMapUpdate, tenantId) =>
      dispatch(doUpdateLinkedSource(tap, fieldMapUpdate, tenantId)),
    fetchLinkedSource: (tap, tenantId = undefined) => dispatch(doFetchLinkedSource(tap, true, tenantId, false)),
    setFieldMapSelection: (newFieldMap) => UIActions.setFieldMapSelection(newFieldMap),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(FieldMapSelector);
